import cuid from 'cuid';

import { type StyleGuideEdit } from '@app/api/styleGuide';
import assertIsNever from '@app/utils/assertIsNever';

import { DROPZONE_FILE_MAX_SIZE_MB } from '../../constants';
import { type RejectedFile } from '../rejectedFile';
import { type FileUpload, type FileUploadStaged, type FileUploadSuccess } from './fileUpload';
import { isFileUploadSuccess } from './fileUploadIsUtils';

export const generateUploadId = cuid;

export const initialiseFileUpload = (file: File): FileUpload => ({
    file,
    uploadId: generateUploadId(),
    state: 'pending',
});

const toUserError = (problem: RejectedFile['problem']): string => {
    switch (problem) {
        case 'file-too-large':
            return `File is larger than ${DROPZONE_FILE_MAX_SIZE_MB}MB`;

        case 'file-invalid-type':
            return 'Only PDF and DOCX files are supported';

        case 'unknown':
            return 'Unable to upload this file';

        default:
            return assertIsNever(problem);
    }
};

export const initialiseRejectedFileUpload = ({ file, problem }: RejectedFile): FileUpload => ({
    file,
    state: 'error',
    uploadId: generateUploadId(),
    error: toUserError(problem),
});

const toStaged = (fileUpload: FileUploadSuccess): FileUploadStaged => ({
    uploadId: fileUpload.uploadId,
    file: fileUpload.file,
    state: 'staged',
    name: fileUpload.file.name,
    styleGuideId: fileUpload.styleGuideId,
});

export const prepareStagedFileUploads = (fileUpload: FileUpload): FileUpload =>
    isFileUploadSuccess(fileUpload) ? toStaged(fileUpload) : fileUpload;

export const fileUploadToStyleGuideEdit = ({ styleGuideId, name, language }: FileUploadStaged): StyleGuideEdit => ({
    id: styleGuideId,
    languageId: language?.id,
    name,
});
